import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Zap, CreditCard, History, Receipt, Inbox, LifeBuoy,
  UserCircle, LogOut, Menu, X,
} from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/predictor", label: "Predictor", icon: Zap },
  { to: "/plans", label: "Plans", icon: CreditCard },
  { to: "/history", label: "History", icon: History },
  { to: "/payment-history", label: "Payments", icon: Receipt },
  { to: "/inbox", label: "Inbox", icon: Inbox },
  { to: "/support", label: "Support", icon: LifeBuoy },
  { to: "/account", label: "Account", icon: UserCircle },
];

export const AppLayout = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
      isActive ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
               : "text-slate-400 hover:text-white hover:bg-slate-800/60 border border-transparent"
    }`;

  const SidebarInner = () => (
    <>
      <div className="px-2 mb-6"><Logo /></div>
      <nav className="space-y-1 flex-1">
        {LINKS.map((l) => (
          <NavLink key={l.to} to={l.to} className={nav} onClick={() => setOpen(false)}
            data-testid={`side-${l.label.toLowerCase()}`}>
            <l.icon size={18} /> {l.label}
          </NavLink>
        ))}
      </nav>
      <button onClick={() => { logout(); navigate("/"); }}
        className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-400 hover:text-red-400 hover:bg-red-500/10 mt-2"
        data-testid="side-logout">
        <LogOut size={18} /> Logout
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0B0F17] flex">
      {/* desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-slate-800 bg-[#0C121C] p-4 sticky top-0 h-screen">
        <SidebarInner />
      </aside>

      {/* mobile topbar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 glass flex items-center justify-between h-14 px-4">
        <Logo size="sm" />
        <button onClick={() => setOpen(true)} className="text-white p-2" data-testid="app-mobile-menu"><Menu size={22} /></button>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="relative w-72 max-w-[80%] bg-[#0C121C] border-r border-slate-800 p-4 flex flex-col">
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-slate-400" data-testid="app-mobile-close"><X size={22} /></button>
            <SidebarInner />
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-10 max-w-6xl mx-auto"><Outlet /></div>
      </main>
    </div>
  );
};
